import React, { ChangeEvent, FC, KeyboardEvent, MouseEvent, Fragment, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import Text from '../../atoms/text/text';
import { IconSearch } from '../../atoms';
import { makeClassNames, IClassNames } from '../../../utils/styles';
import List, { cn as listClassNames } from '../../atoms/list/list';
import t from '../../../labels/labelling-utils';

import './search-input.scss';

export interface ISearchInput {
  isLoading?: boolean;
  options: Array<ISearchOption>;
  title?: string;
  placeholder?: string;
  onClickCallback?: (_: ISearchOption) => void;
}

export interface ISearchOption {
  id: string;
  name: string;
  url: string;
}

const classNames = (): IClassNames =>
  makeClassNames({
    inputContainer: 'search-input--container',
    input: 'search-input',
    searchInputWrapper: 'search-input--wrapper',
    mobileMenuIsOpen: 'mobile-menu--is-open',
    mobileIconButton: 'mobile-icon-button',
    mobileMenuCloseButton: 'mobile-close-button',
    title: 'mobile-title',
    mobileCancel: 'mobile-cancel',
  });

const scrollSelected = (refSelect: React.RefObject<HTMLDivElement>) => {
  const selector: string = `.${listClassNames.listItemActive}`;
  const selected: Element | null | undefined = refSelect.current?.querySelector(selector);

  // Don't mock WINDOW global functions for testing, "scrollIntoView" will be probably moved to WebApp
  /* istanbul ignore if */
  if (selected && typeof selected.scrollIntoView === 'function') {
    selected.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    });
  }
};

/*
 * On desktop, when simulating mobile navigation
 * and using the icon template
 * a scrollbar is visible.
 * This issue could e solved by using ReactModal (?)
 */
const SearchInput: FC<ISearchInput> = ({
  /* istanbul ignore next: default value */ isLoading = false,
  options,
  title,
  placeholder,
  onClickCallback,
}) => {
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState<number>(0);
  const [dataUrl, setDataUrl] = useState<string>('');
  const [filteredSuggestions, setFilteredSuggestions] = useState<Array<ISearchOption>>([]);
  const [input, setInput] = useState<string>('');
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [mobileMenuIsOpen, setMobileMenuIsOpen] = useState<boolean>(false);
  const refSelect: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollSelected(refSelect);
  }, [activeSuggestionIndex]);

  const cn = classNames();

  const setUserInput = (inputValue: string): void => {
    setInput(inputValue.trim());
    const municipality: ISearchOption | undefined = options.find(
      (municipality: ISearchOption) => municipality.name.toLowerCase() === inputValue.toLowerCase()
    );
    setDataUrl(municipality?.url ?? '');
  };

  const onChange = (evt: ChangeEvent<HTMLInputElement>) => {
    const inputValue: string = evt.target.value;

    // Filter options based on user input if user wrote at least 2 characters
    const hasLength: boolean = inputValue.length >= 2;
    if (hasLength) {
      const filteredSuggestions: Array<ISearchOption> = options
        .filter((suggestion: ISearchOption) => suggestion.name.toLowerCase().startsWith(inputValue.toLowerCase()))
        .sort((prev, next) => prev.name.localeCompare(next.name));
      setFilteredSuggestions(filteredSuggestions);
      setActiveSuggestionIndex(0);
    }

    setShowSuggestions(hasLength);
    setInput(inputValue);
    setDataUrl(''); // Reset PREVIOUS find
  };

  const onClick = (evt: MouseEvent<HTMLElement>) => {
    const clickedValue: string = (evt.target as HTMLElement).innerHTML;
    const index: number = Number((evt.target as HTMLElement).getAttribute('data-index') ?? '0');

    setUserInput(clickedValue);
    setFilteredSuggestions([]);
    setActiveSuggestionIndex(0);
    setShowSuggestions(false);

    onClickCallback && onClickCallback(filteredSuggestions[index]);
  };

  const onKeyDown = (evt: KeyboardEvent) => {
    switch (evt.key) {
      case 'ArrowUp':
        if (activeSuggestionIndex > 0) {
          setActiveSuggestionIndex(activeSuggestionIndex - 1);
        }
        break;
      case 'ArrowDown':
        if (activeSuggestionIndex < filteredSuggestions.length - 1) {
          setActiveSuggestionIndex(activeSuggestionIndex + 1);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setUserInput('');
        break;
      case 'Enter':
        {
          const municipality: ISearchOption | undefined = filteredSuggestions[activeSuggestionIndex];
          if (municipality && municipality.name) {
            setActiveSuggestionIndex(0);
            setShowSuggestions(false);
            setUserInput(municipality.name);
            onClickCallback && onClickCallback(municipality);
          }
        }
        break;
      default:
        break; // All other keys
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuIsOpen((prevState) => !prevState);
  };

  return (
    <Fragment>
      <button className={cn.mobileIconButton} type="button" onClick={toggleMobileMenu}>
        <IconSearch size="xsmall" color="dark" />
      </button>
      <div className={`${cn.searchInputWrapper} ${mobileMenuIsOpen ? cn.mobileMenuIsOpen : ''}`}>
        <button className={cn.mobileMenuCloseButton} onClick={toggleMobileMenu} type="button">
          ✕
        </button>
        {title && (
          <Text className={cn.title} fontSize="22" fontWeight="500" tag="p">
            {title}
          </Text>
        )}
        <div className={cn.inputContainer}>
          <input
            type="text"
            placeholder={placeholder || t('search-input.search.your.municipality')}
            className={cn.input}
            data-url={dataUrl}
            value={input}
            disabled={isLoading}
            onChange={onChange}
            onKeyDown={onKeyDown}
          />
          <IconSearch size="xxsmall" color="dark" />
        </div>
        <div className={cn.mobileCancel} onClick={() => setUserInput('')}>
          <Text fontSize="14" fontWeight="400" tag="span">
            {t('search-input.cancel')}
          </Text>
        </div>
        {showSuggestions && input && (
          <div ref={refSelect}>
            <List
              listItems={filteredSuggestions.map((suggestion: ISearchOption) => suggestion.name)}
              activeItemIndex={activeSuggestionIndex}
              onClick={onClick}
              emptyListMessage={t('search-input.no.matches.found')}
            />
          </div>
        )}
      </div>
    </Fragment>
  );
};

SearchInput.propTypes = {
  isLoading: PropTypes.bool,
  onClickCallback: PropTypes.func,
  options: PropTypes.arrayOf<ISearchOption>(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    }).isRequired
  ).isRequired,
  placeholder: PropTypes.string,
  title: PropTypes.string,
};

export default SearchInput;
